/**
 * Fanatic Account Settings (AC-FANATIC-007, AC-FANATIC-008).
 *
 * Five sections, each backed by a real endpoint:
 *
 * | Section          | Endpoint                                         |
 * | ---------------- | ------------------------------------------------ |
 * | Email            | `PATCH /auth/v1/account` (fanatic-only)           |
 * | Password         | `POST /auth/v1/password/reset/request` (no tenant)|
 * | Sign-in methods  | `GET`/`DELETE /auth/v1/account/identities`        |
 * | Two-factor       | `POST /auth/v1/mfa/enroll` + `/mfa/verify`        |
 * | Followed teams   | `GET`/`DELETE /fanatic/v1/grants`                  |
 *
 * Email and password are genuinely self-service (recorded product decision,
 * grind answer #2): the auth service exposes a fanatic-scoped self-PATCH for
 * the email (re-authenticated with the current password when one exists) and
 * accepts tenant-less reset requests, resolving them against the shared fan
 * realm. The earlier support-desk routing is gone.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useApiClient } from '../../app/providers';
import { useSession } from '../../app/session-context';
import { useToast } from '../../app/toast-context';
import { ConfirmDialog } from '../../components/confirm-dialog';
import { TextField } from '../../components/text-field';
import type { FanaticGrant, LinkedIdentity, MfaEnrollment } from '../../lib/api-types';
import { FANATIC_VIEWS } from '../../lib/constants';

import { toFanaticMessage, useFanaticPortal } from './fanatic-portal';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/u;
const TOTP_CODE_LENGTH = 6;

function grantLabel(grant: FanaticGrant): string {
  return grant.team_name ?? grant.team_slug ?? 'this team';
}

function formatDate(iso: string | null): string {
  if (iso === null) return 'unknown';
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? 'unknown' : parsed.toLocaleDateString();
}

export function FanaticAccountSettingsPage(): JSX.Element {
  const { claims } = useSession();
  const { activeGrants, grants, reload, selectTeam, withTeam } = useFanaticPortal();
  const accountEmail = claims?.email ?? null;

  return (
    <>
      <section className="fan-panel" aria-label="Account">
        <div className="fan-panel__head">
          <h2 className="fan-panel__title">Account</h2>
        </div>
        <ul className="fan-list">
          <li className="fan-list__row">
            <div className="fan-list__main">
              <span className="fan-list__name">{accountEmail ?? 'Unknown email'}</span>
              <span className="fan-list__meta">
                Spectator account · shared fan realm, not tied to one team
              </span>
            </div>
          </li>
        </ul>
        <EmailChangeForm />
        <PasswordResetSection currentEmail={accountEmail} />
      </section>

      <SignInMethodsSection />
      <TwoFactorSection />

      <FollowedTeamsSection
        activeGrants={activeGrants}
        grants={grants}
        onSwitch={selectTeam}
        onUnfollowed={reload}
        withTeam={withTeam}
      />

      <p className="fanatic__aggregate-note">
        Looking for more games?{' '}
        <Link to={withTeam(FANATIC_VIEWS.browse)}>Find more teams to follow</Link>.
      </p>
    </>
  );
}

// --------------------------------------------------------------- email/password

function EmailChangeForm(): JSX.Element {
  const apiClient = useApiClient();
  const { showToast } = useToast();
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [changedTo, setChangedTo] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    const email = newEmail.trim();
    if (!EMAIL_PATTERN.test(email)) {
      setFieldError('Enter a valid email address, for example fan@example.com.');
      return;
    }
    setIsBusy(true);
    setError(null);
    setFieldError(null);
    try {
      const result = await apiClient.changeAccountEmail({
        email,
        ...(currentPassword === '' ? {} : { currentPassword }),
      });
      setNewEmail('');
      setCurrentPassword('');
      setChangedTo(result.email);
      showToast('Email updated.', 'success');
    } catch (cause) {
      setError(toFanaticMessage(cause, 'We could not update your email. Please try again.'));
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div className="fan-panel__body">
      <h3 className="fan-panel__title">Change your email</h3>
      <p className="fan-panel__hint">
        We ask for your current password so nobody can move your account to an address you do not
        own. Signed up with Google or Apple only? Leave the password blank.
      </p>
      {error !== null ? (
        <p className="banner banner--error" role="alert">
          {error}
        </p>
      ) : null}
      {changedTo !== null ? (
        <p className="banner banner--success" role="status">
          Email updated to {changedTo}. Use it the next time you sign in.
        </p>
      ) : null}
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <TextField
          autoComplete="email"
          error={fieldError}
          label="New email address"
          name="new-email"
          onChange={(event) => setNewEmail(event.target.value)}
          type="email"
          value={newEmail}
        />
        <TextField
          autoComplete="current-password"
          label="Current password"
          name="current-password"
          onChange={(event) => setCurrentPassword(event.target.value)}
          type="password"
          value={currentPassword}
        />
        <button className="btn btn--secondary" disabled={isBusy} type="submit">
          {isBusy ? 'Saving…' : 'Change email'}
        </button>
      </form>
    </div>
  );
}

function PasswordResetSection({ currentEmail }: { currentEmail: string | null }): JSX.Element {
  const apiClient = useApiClient();
  const { showToast } = useToast();
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expiresInHours, setExpiresInHours] = useState<number | null>(null);
  const [isSent, setIsSent] = useState(false);

  const submit = async (): Promise<void> => {
    if (currentEmail === null) {
      setError('We could not read your account email. Sign out and back in, then try again.');
      return;
    }
    setIsBusy(true);
    setError(null);
    try {
      // Tenant-less on purpose: Fanatic accounts live in the shared fan realm.
      const result = await apiClient.requestPasswordReset({ email: currentEmail });
      setExpiresInHours(result.expires_in_hours);
      setIsSent(true);
      showToast('Password reset link sent.', 'success');
    } catch (cause) {
      setError(toFanaticMessage(cause, 'We could not send the reset email. Please try again.'));
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div className="fan-panel__body">
      <h3 className="fan-panel__title">Change your password</h3>
      <p className="fan-panel__hint">
        We email {currentEmail ?? 'your account address'} a single-use reset link rather than
        accepting a new password on this page.
      </p>
      {error !== null ? (
        <p className="banner banner--error" role="alert">
          {error}
        </p>
      ) : null}
      {isSent ? (
        <p className="banner banner--success" role="status">
          Reset link sent to {currentEmail ?? 'your account address'}
          {expiresInHours !== null ? ` (valid for ${expiresInHours} hours)` : ''}. The link works
          once.
        </p>
      ) : null}
      <button
        className="btn btn--secondary"
        disabled={isBusy}
        onClick={() => void submit()}
        type="button"
      >
        {isBusy ? 'Sending…' : 'Email me a reset link'}
      </button>
    </div>
  );
}

// ------------------------------------------------------------- sign-in methods

function SignInMethodsSection(): JSX.Element {
  const apiClient = useApiClient();
  const { showToast } = useToast();
  const [identities, setIdentities] = useState<LinkedIdentity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<LinkedIdentity | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    void (async () => {
      try {
        const page = await apiClient.listLinkedIdentities(controller.signal);
        setIdentities(page.data);
        setError(null);
      } catch (cause) {
        if (controller.signal.aborted) return;
        setError(toFanaticMessage(cause, 'We could not load your sign-in methods.'));
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    })();
    return () => controller.abort();
  }, [apiClient, reloadToken]);

  const unlink = async (identity: LinkedIdentity): Promise<void> => {
    try {
      await apiClient.unlinkIdentity(identity.identity_id);
      showToast(`${identity.provider} unlinked.`, 'success');
      setPending(null);
      setDialogError(null);
      setReloadToken((token) => token + 1);
    } catch (cause) {
      setDialogError(toFanaticMessage(cause, 'We could not unlink that provider.'));
    }
  };

  return (
    <section className="fan-panel" aria-label="Sign-in methods">
      <div className="fan-panel__head">
        <h2 className="fan-panel__title">Sign-in methods</h2>
      </div>
      <p className="fan-panel__hint">
        Signing in again with any linked provider returns you to this same account — your followed
        teams stay exactly as they are (AC-FANATIC-008). To add a provider, sign in with it using
        this same email address and we link it automatically.
      </p>
      {error !== null ? (
        <p className="banner banner--error" role="alert">
          {error}
        </p>
      ) : null}
      {isLoading ? <p className="fan-empty">Loading sign-in methods…</p> : null}
      {!isLoading && error === null && identities.length === 0 ? (
        <p className="fan-empty">
          No social sign-in linked yet — you sign in with your email and password.
        </p>
      ) : null}
      {identities.length > 0 ? (
        <ul className="fan-list">
          {identities.map((identity) => (
            <li className="fan-list__row" key={identity.identity_id}>
              <div className="fan-list__main">
                <span className="fan-list__name">{identity.provider}</span>
                <span className="fan-list__meta">
                  {identity.email ?? 'no email on file'} · linked {formatDate(identity.linked_at)}
                </span>
              </div>
              <div className="fan-list__actions">
                <span className="fan-badge fan-badge--active">Linked</span>
                <button
                  className="btn btn--danger btn--sm"
                  disabled={!identity.can_unlink}
                  onClick={() => setPending(identity)}
                  title={
                    identity.can_unlink
                      ? undefined
                      : 'This is your only way to sign in, so it cannot be unlinked.'
                  }
                  type="button"
                >
                  Unlink
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {pending !== null ? (
        <ConfirmDialog
          confirmLabel="Unlink provider"
          consequence={`You will no longer be able to sign in with ${pending.provider}.`}
          error={dialogError}
          onCancel={() => {
            setPending(null);
            setDialogError(null);
          }}
          onConfirm={() => unlink(pending)}
          title={`Unlink ${pending.provider}?`}
          tone="danger"
        />
      ) : null}
    </section>
  );
}

// ----------------------------------------------------------------- two-factor

function TwoFactorSection(): JSX.Element {
  const apiClient = useApiClient();
  const { showToast } = useToast();
  const { session, applyMfaVerification } = useSession();
  const [enrollment, setEnrollment] = useState<MfaEnrollment | null>(null);
  const [code, setCode] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const isEnrolled = session?.isMfaEnrolled === true;

  const start = async (): Promise<void> => {
    setIsBusy(true);
    setError(null);
    try {
      setEnrollment(await apiClient.enrollMfa());
    } catch (cause) {
      setError(toFanaticMessage(cause, 'We could not start two-factor enrolment.'));
    } finally {
      setIsBusy(false);
    }
  };

  const verify = async (): Promise<void> => {
    const trimmed = code.trim();
    if (trimmed.length !== TOTP_CODE_LENGTH || !/^\d+$/u.test(trimmed)) {
      setFieldError(`Enter the ${TOTP_CODE_LENGTH}-digit code from your authenticator app.`);
      return;
    }
    setIsBusy(true);
    setFieldError(null);
    try {
      applyMfaVerification(await apiClient.verifyMfa(trimmed));
      setEnrollment(null);
      setCode('');
      showToast('Two-factor authentication is on.', 'success');
    } catch (cause) {
      setFieldError(toFanaticMessage(cause, 'That code was not accepted. Try the next one.'));
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <section className="fan-panel" aria-label="Two-factor authentication">
      <div className="fan-panel__head">
        <h2 className="fan-panel__title">Two-factor authentication</h2>
        <span className={`fan-badge ${isEnrolled ? 'fan-badge--active' : 'fan-badge--revoked'}`}>
          {isEnrolled ? 'On' : 'Off'}
        </span>
      </div>
      <p className="fan-panel__hint">
        Optional for spectator accounts. Turning it on asks for a 6-digit code from your
        authenticator app the next time you sign in.
      </p>
      {error !== null ? (
        <p className="banner banner--error" role="alert">
          {error}
        </p>
      ) : null}

      {enrollment === null ? (
        <div className="fan-panel__actions">
          <button
            className="btn btn--secondary"
            disabled={isBusy}
            onClick={() => void start()}
            type="button"
          >
            {isBusy
              ? 'Working…'
              : isEnrolled
                ? 'Replace authenticator app'
                : 'Enable two-factor authentication'}
          </button>
        </div>
      ) : (
        <div className="fan-panel__body">
          <p className="fan-panel__hint">
            Add this key to your authenticator app, then enter the code it shows.
          </p>
          <p className="fan-invite-link">
            <span className="fan-invite-link__url">{enrollment.totp_uri}</span>
          </p>
          <p className="fan-panel__hint">
            Save these one-time backup codes somewhere safe — each works once:
          </p>
          <ul className="fan-list">
            {enrollment.backup_codes.map((backupCode) => (
              <li className="fan-list__row mono" key={backupCode}>
                {backupCode}
              </li>
            ))}
          </ul>
          <form
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              void verify();
            }}
          >
            <TextField
              autoComplete="one-time-code"
              error={fieldError}
              inputMode="numeric"
              label="6-digit code"
              name="totp-code"
              onChange={(event) => setCode(event.target.value)}
              value={code}
            />
            <div className="fan-panel__actions">
              <button className="btn btn--primary" disabled={isBusy} type="submit">
                {isBusy ? 'Checking…' : 'Confirm and turn on'}
              </button>
              <button
                className="btn btn--ghost"
                onClick={() => {
                  setEnrollment(null);
                  setFieldError(null);
                }}
                type="button"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}

// -------------------------------------------------------------- followed teams

function FollowedTeamsSection({
  grants,
  activeGrants,
  onSwitch,
  onUnfollowed,
  withTeam,
}: {
  grants: readonly FanaticGrant[];
  activeGrants: readonly FanaticGrant[];
  onSwitch: (grant: FanaticGrant) => void;
  onUnfollowed: () => void;
  withTeam: (view: string) => string;
}): JSX.Element {
  const apiClient = useApiClient();
  const { showToast } = useToast();
  const [pending, setPending] = useState<FanaticGrant | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);

  const unfollow = useCallback(
    async (grant: FanaticGrant): Promise<void> => {
      try {
        await apiClient.unfollowTeam(grant.grant_id);
        showToast(`You no longer follow ${grantLabel(grant)}.`, 'success');
        setPending(null);
        setDialogError(null);
        onUnfollowed();
      } catch (cause) {
        setDialogError(toFanaticMessage(cause, 'We could not unfollow that team.'));
      }
    },
    [apiClient, onUnfollowed, showToast]
  );

  const pendingInvites = grants.filter((grant) => grant.status === 'pending_invite');

  return (
    <section className="fan-panel" aria-label="Followed teams">
      <div className="fan-panel__head">
        <h2 className="fan-panel__title">Followed teams</h2>
        <span className="fan-panel__hint">
          {activeGrants.length} following
          {pendingInvites.length > 0 ? ` · ${pendingInvites.length} invite pending` : ''}
        </span>
      </div>

      {activeGrants.length === 0 ? (
        <p className="fan-empty">
          You are not following any teams yet.{' '}
          <Link to={withTeam(FANATIC_VIEWS.browse)}>Find a team</Link>.
        </p>
      ) : (
        <ul className="fan-list">
          {activeGrants.map((grant) => (
            <li className="fan-list__row" key={grant.grant_id}>
              <div className="fan-list__main">
                <span className="fan-list__name">{grantLabel(grant)}</span>
                <span className="fan-list__meta">
                  Following since {formatDate(grant.granted_at)}
                  {grant.granted_by === null ? ' · you followed this team' : ' · team invited you'}
                </span>
              </div>
              <div className="fan-list__actions">
                <button
                  className="btn btn--secondary btn--sm"
                  onClick={() => onSwitch(grant)}
                  type="button"
                >
                  View team
                </button>
                <button
                  className="btn btn--danger btn--sm"
                  onClick={() => setPending(grant)}
                  type="button"
                >
                  Unfollow
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {pending !== null ? (
        <ConfirmDialog
          confirmLabel="Unfollow team"
          consequence={`You will no longer receive updates for ${grantLabel(
            pending
          )}. Are you sure?`}
          error={dialogError}
          onCancel={() => {
            setPending(null);
            setDialogError(null);
          }}
          onConfirm={() => unfollow(pending)}
          title={`Unfollow ${grantLabel(pending)}?`}
          tone="danger"
        />
      ) : null}
    </section>
  );
}
